import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { WBSButton } from '@/common/WBSButton';
import FollowUpRequestsTable from '@/tables/FollowUpRequestsTable';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { GroupHeaderWrapper } from '~/styles/layoutElements';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/follow-ups/follow-up-requests/',
)({
  component: FollowUpRequestsScreen,
});

function FollowUpRequestsScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { groupName } = Route.useParams();
  const { me } = useCurrentUser();
  const { data: groupData } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });

  // DIV-20 — the exact profile type, not `useCurrentUser().isStudyHelper`,
  // which folds ADMIN in with STUDY_HELPER.
  //
  // Hiding the button from a platform admin IS a divergence: `hasAccess()`
  // admits the `administrator` BuddyPress member type
  // (`class-study-group-follow-up.php:44`, mapped to `ProfileType.ADMIN` by
  // `migration/users.py:68-69`) and `follow-up-requests.php:4-6` renders the
  // button with no capability test of any kind, so legacy did show it to them.
  // It ships anyway because the control cannot work for that caller:
  // `follow_up_requests.creator_id` FKs `study_helpers.id` NOT NULL and 0 of 18
  // admins have a row, so `POST` 403s them on `CurrentStudyHelperDependency` and
  // legacy's equivalent click is a 500-shaped dead end.
  const isStudyHelper = me?.profile_type === 'STUDY_HELPER';

  const handleCreateRequest = () => {
    navigate({
      to: '/groups/$groupName/follow-ups/follow-up-requests/new/',
      params: { groupName },
      search: { user_id: undefined },
    });
  };

  return (
    <>
      <GroupHeaderWrapper>
        {/* `helpKey` is the legacy section id, not the tab title: `HelpModal`
            matches lowercased ENGLISH ids and falls back SILENTLY to the first
            item, so a translated title opens the Courses help for every
            non-English user. The group tab's own help post is not the member
            tab's (`student_follow_ups`), which is why W4.3's key is not reused. */}
        <TitleWithQuestion
          title={t('wbs.follow-up-requests')}
          helpKey="follow-up requests"
        />
        {isStudyHelper && (
          <WBSButton wide onClick={handleCreateRequest}>
            {t('actions.create-a-new-request')}
          </WBSButton>
        )}
      </GroupHeaderWrapper>
      <FollowUpRequestsTable
        groupId={groupData?.id ?? 0}
        groupName={groupData?.name ?? ''}
        groupSlug={groupName}
      />
    </>
  );
}
